import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import IncomingTransferAcceptModal from "./IncomingTransferAcceptModal";
import {
  fetchIncomingTransfers,
  listProjects,
  unwrapListPayload,
} from "../../services/investmentService";
import { filterInvestorCatalogProjects, projectTitleWithCode } from "../../utils/projectViews";
import { useAuth } from "../../store/AuthContext";

export default function IncomingTransfersAcceptCard({ onAccepted, onError }) {
  const { user } = useAuth();
  const kycApproved = String(user?.kyc_status || "").toUpperCase() === "APPROVED";
  const [projects, setProjects] = useState([]);
  const [incomingTransfers, setIncomingTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptTx, setAcceptTx] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [incomingRes, projectsRes] = await Promise.all([
        fetchIncomingTransfers().catch(() => ({ data: [] })),
        listProjects().catch(() => ({ data: [] })),
      ]);
      setIncomingTransfers(Array.isArray(incomingRes?.data) ? incomingRes.data : []);
      setProjects(filterInvestorCatalogProjects(unwrapListPayload(projectsRes.data)));
    } catch {
      setIncomingTransfers([]);
      onError?.("Could not load incoming transfer offers.");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading && incomingTransfers.length === 0) {
    return null;
  }

  if (!incomingTransfers.length) {
    return null;
  }

  const acceptProject = acceptTx
    ? projects.find((x) => String(x.id) === String(acceptTx.project))
    : null;

  return (
    <>
      <section className="investor-section investor-incoming-transfers" aria-labelledby="incoming-transfers-title">
        <h3 id="incoming-transfers-title" className="investor-section__title">
          Transfers awaiting your acceptance
        </h3>
        <p className="investor-section__desc" style={{ marginTop: 0 }}>
          NAV-locked offers sent to you. Open <strong>Review &amp; accept</strong> to see settlement and confirm.
        </p>
        {!kycApproved ? (
          <p className="msg msg-error" style={{ marginTop: 8 }}>
            Approved KYC is required before accepting share transfers. <Link to="/kyc">Complete KYC →</Link>
          </p>
        ) : null}
        <ul className="investor-incoming-transfers__list">
          {incomingTransfers.map((tx) => {
            const txProj = projects.find((x) => String(x.id) === String(tx.project));
            const projLabel = txProj ? projectTitleWithCode(txProj) : String(tx.project || "");
            const tr = tx.transfer_record;
            return (
              <li key={tx.id} className="investor-incoming-transfers__item">
                <span className="investor-incoming-transfers__summary">
                  <strong>{projLabel}</strong>
                  <span className="muted"> · {tr?.shares_offered ?? tx.shares} sh</span>
                </span>
                <button
                  type="button"
                  className="btn-secondary compact-btn"
                  disabled={!kycApproved}
                  onClick={() => setAcceptTx(tx)}
                >
                  Review &amp; accept
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {acceptTx && kycApproved ? (
        <IncomingTransferAcceptModal
          tx={acceptTx}
          project={acceptProject}
          onClose={() => setAcceptTx(null)}
          onAccepted={() => {
            refresh();
            onAccepted?.();
          }}
        />
      ) : null}
    </>
  );
}
